/**
 * ProfileSignInPage — email-only sign-in for returning customers.
 * Looks up the most recent completed application and routes the user
 * directly into their /my-matches/:sessionId profile, where they can
 * retake the application or browse past attempts.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Mail, Loader2, ChevronRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileSignInPage = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [autoMode, setAutoMode] = useState(false);
  const triggeredRef = useRef(false);

  const performSignIn = async (rawEmail) => {
    const v = (rawEmail || '').trim().toLowerCase();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/profile/email-signin`, { email: v });
      if (res.data?.latest_session_id) {
        toast.success(`Welcome back${res.data.first_name ? `, ${res.data.first_name}` : ''}!`);
        navigate(`/my-matches/${res.data.latest_session_id}`, { replace: true });
      } else {
        throw new Error('Missing session id');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 404) {
        toast.error(detail || 'No application found for this email yet.');
      } else {
        toast.error(detail || 'Could not sign you in. Try again.');
      }
      setSubmitting(false);
      // Drop out of auto mode so user can edit / retry manually
      setAutoMode(false);
    }
  };

  // Magic-link auto sign-in: when arriving from an email with ?email=<addr>,
  // skip the form and sign the user straight into their latest dashboard.
  useEffect(() => {
    const fromQuery = searchParams.get('email');
    if (fromQuery && !triggeredRef.current) {
      triggeredRef.current = true;
      const decoded = decodeURIComponent(fromQuery);
      setEmail(decoded);
      setAutoMode(true);
      performSignIn(decoded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    await performSignIn(email);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950' : 'bg-gradient-to-br from-[#004E86] via-[#003d6b] to-[#002d50]'}`} data-testid="profile-signin-page">
      <div className="max-w-md mx-auto px-4 sm:px-6 pt-10 pb-16">
        <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className={`rounded-3xl ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'} shadow-2xl p-7`}>
          {autoMode && submitting ? (
            <div className="text-center py-8" data-testid="signin-auto-loading">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#F2A900]/15 text-[#F2A900] mb-4">
                <Loader2 className="w-7 h-7 animate-spin" />
              </div>
              <h1 className={`text-2xl sm:text-3xl font-extrabold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Signing you in…
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Loading your matches for <span className="font-mono">{email}</span>
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#F2A900]/15 text-[#F2A900] mb-4">
                  <Mail className="w-7 h-7" />
                </div>
                <h1 className={`text-2xl sm:text-3xl font-extrabold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Welcome back
                </h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Sign in with the email you used on the application to see your matches & past attempts.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label htmlFor="signin-email" className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Your email
                  </label>
                  <Input
                    id="signin-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    data-testid="signin-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#F2A900] hover:bg-[#E89400] text-black font-bold py-6 text-base rounded-2xl disabled:opacity-60"
                  data-testid="signin-submit-btn"
                >
                  {submitting ? (
                    <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Signing you in…</span>
                  ) : (
                    <span className="inline-flex items-center gap-2">Continue <ChevronRight className="w-4 h-4" /></span>
                  )}
                </Button>
              </form>

              <div className={`mt-5 pt-5 text-center text-sm border-t ${isDark ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
                New here?{' '}
                <Link to="/" className="font-semibold text-[#F2A900] hover:underline">
                  Take the RV Match application →
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSignInPage;
